import Subscribable from 'common/provider/subscribable.provider';

export default class SessionService extends Subscribable() {
    /* @ngInject */
    constructor($q, apiService, storageService, appStartCacheService, diagnosticsService) {
        super();

        this.$q = $q;
        this.apiService = apiService;
        this.storageService = storageService;
        this.appStartCacheService = appStartCacheService;
        this.diagnosticsService = diagnosticsService;

        this.promise = null;
    }

    reload() {
        return this.initialize(true).catch((error) => {
            return this.$q.reject(error);
        });
    }

    initialize(reload) {
        if (!reload && this.promise) {
            return this.promise;
        }

        this.promise = this.apiService.onlyAuthorized()
            .then(() => {
                return this.apiService.get(`/context`)
                    .then((result) => {
                        return this.updateUser(result);
                    })
                    .catch((error) => {
                        this.clearUser();
                        return this.$q.reject(error);
                    });
            })
            .catch((error) => {
                this.clearUser();
                return this.$q.reject(error);
            });
        
        return this.promise;
    }

    updateUser(result) {
        if (result.data && result.data.user) {
            this.user = result.data.user;
            return result.data;
        }

        this.clearUser();
        return this.$q.reject(`We were unable to read and process your initial request. Please try again later.`);
    }

    deleteAccount() {
        return this.apiService.delete('/users/me')
            .finally(() => {
                this.changeTokenAndReload();
            });
    }

    clearUser() {
        this.user = null;
    }

    signIn(identity, password) {
        this.diagnosticsService.info('sign_in_attempt', {
            identityProvided: !!identity
        });

        return this.apiService.post(`/sign-in`, {
            identity,
            password
        }, {
            anonymous: true
        })
            .then((result) => {
                return this.changeTokenAndReload(result)
                    .then((data) => {
                        if (!data || !data.user) {
                            this.diagnosticsService.error('sign_in_missing_user_after_reload');
                            return this.$q.reject(new Error('No user data after reload'));
                        }
                        this.diagnosticsService.info('sign_in_success', {
                            userId: data.user && data.user.id
                        });
                        return this.fire('sign-in', data.user);
                    });
            })
            .catch((error) => {
                this.diagnosticsService.warn('sign_in_failed', this.diagnosticsService.describeHttpError(error));
                return this.$q.reject(error || new Error('Sign in failed'));
            });
    }

    signUp(name, identity, password) {
        return this.apiService.post(`/sign-up`, {
            name,
            identity,
            password,
            email: identity
        }, {
            anonymous: true
        })
            .then(() => {
                return this.signIn(identity, password);
            })
            .catch((error) => {
                return this.$q.reject(error);
            });
    }

    signOut() {
        this.diagnosticsService.info('sign_out_requested');
        return this.apiService.post(`/sign-out`)
            .then((result) => {
                this.finalizeSignOut();
                this.diagnosticsService.info('sign_out_completed');
                return result;
            })
            .catch((error) => {
                this.diagnosticsService.warn('sign_out_failed', this.diagnosticsService.describeHttpError(error));
                return this.$q.reject(error);
            });
    }

    finalizeSignOut() {
        // Clear the token and user data after a successful sign-out.
        this.appStartCacheService.clearAll();
        this.apiService.token = null;
        this.promise = null;
        this.storageService.setUserPrefix(null);
        this.clearUser();
        this.fire('change', null);
    }

    refreshToken() {
        this.diagnosticsService.info('refresh_token_attempt');
        return this.apiService.post(`/refresh-token`, null, {
            anonymous: true
        }).then((result) => {
            this.diagnosticsService.info('refresh_token_success');
            return this.changeToken(result);
        }).catch((error) => {
            this.diagnosticsService.warn('refresh_token_failed', this.diagnosticsService.describeHttpError(error));
            return this.$q.reject(error);
        });
    }

    changeToken(result) {
        const token = result && result.data && result.data.token ? result.data.token : null;

        if (!token) {
            this.diagnosticsService.error('change_token_missing_token');
            return this.$q.reject(new Error('No token received'));
        }

        this.apiService.token = token;
        this.fire('change', token);

        return this.$q.resolve(token);
    }

    changeTokenAndReload(result) {
        return this.changeToken(result)
            .then(() => {
                return this.apiService.onlyAuthorized()
                    .then(() => {
                        return this.reload();
                    });
            })
            .catch((error) => {
                return this.reload().then(() => this.$q.reject(error));
            });
    }

    onChange(callback) {
        return this.on('change', callback);
    }

    onSignIn(callback) {
        return this.on('sign-in', callback);
    }
}



// WEBPACK FOOTER //
// ./src/application/service/session.service.js